// ui/src/lib/logger/sanitize.ts
const SENSITIVE_KEYWORDS = [
  "password",
  "token",
  "jwt",
  "secret",
  "ssn",
  "creditcard",
  "privatekey",
  "private_key",
  "authorization",
  "apikey",
  "api_key"
];

/**
 * Recursively sanitizes an object to remove sensitive data.
 * @param obj The object to sanitize.
 * @returns A new, sanitized object.
 */
/**
 * Recursively sanitizes an object to remove sensitive data.
 * Handles circular references to prevent stack overflow.
 * @param obj The object to sanitize.
 * @param seen Internal WeakSet to track visited objects (for circular refs).
 * @returns A new, sanitized object.
 */
export function sanitizeLogData(obj: any, seen?: WeakSet<object>): any {
  // Handle DOMStringMap (dataset) by converting to a plain object
  if (typeof DOMStringMap !== "undefined" && obj instanceof DOMStringMap) {
    return { ...obj };
  }
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (!seen) {
    seen = new WeakSet();
  }
  if (seen.has(obj)) {
    return "[CIRCULAR]";
  }
  seen.add(obj);

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeLogData(item, seen));
  }

  const sanitized: Record<string, unknown> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const lowerCaseKey = key.toLowerCase();
      // Check if the key itself is a sensitive keyword
      if (SENSITIVE_KEYWORDS.some((keyword) => lowerCaseKey.includes(keyword))) {
        sanitized[key] = "[REDACTED]";
        continue;
      }

      const value = obj[key];
      // Check if the value is a string and contains sensitive information
      if (typeof value === "string" && SENSITIVE_KEYWORDS.some((keyword) => value.toLowerCase().includes(keyword))) {
        sanitized[key] = "[REDACTED]";
      } else if (typeof value === "object" && value !== null) {
        // Recurse for nested objects, handle circular refs
        sanitized[key] = sanitizeLogData(value, seen);
      } else {
        sanitized[key] = value;
      }
    }
  }
  return sanitized;
}
